import React, { useContext } from 'react';
import { Box, Card } from '@mui/material';
import { borderRadius, boxShadows, customSizes, palette } from '../../../assets/styles/themes/tokens';
import ArtifactCardHeader from './ArtifactCardHeader';
import ArtifactCardContent from './ArtifactCardContent';
import ArtifactCardImageContent from './ArtifactCardImageContent';
import useRouter from '../../../utils/useRouter';
import createNonPropagatingEventHandler from '../../../utils/createNonPropagatingEventHandler';
import { aiAssistantPageSource, amplitudeContentTypes, routes } from '../../../constants';
import { GeneratedArtifact, GeneratedArtifactType } from '@klab-berlin/api-sdk/lib/types/responses/Ai';
import { isGeneratingPodcastArtifact } from '../../../utils/artifacts';
import CircularProgressFixed from '../../../components/utils/CircularProgressFixed';
import services from '../../../services';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';

interface ArtifactCardProps {
  artifact: GeneratedArtifact;
}

const ArtifactCard: React.FC<ArtifactCardProps> = ({ artifact }) => {
  const { id, title, type, conversationId, createdAt } = artifact;
  const presignedUrl = 'presignedUrl' in artifact ? artifact.presignedUrl : undefined;
  const { router } = useRouter();
  const { preparePodcastNavigation } = useContext(aiAssistantContext);

  const isImage = type === GeneratedArtifactType.IMAGE;
  const isPodcast = type === GeneratedArtifactType.PODCAST;
  const isGeneratingPodcast = isGeneratingPodcastArtifact(artifact);

  const headerCreatedAt = isGeneratingPodcast ? undefined : createdAt;

  // Documents carry their preview text in `content`, podcasts in `transcript`.
  const getTextContent = () => {
    if ('content' in artifact) return artifact.content;
    if ('transcript' in artifact) return artifact.transcript;
    return '';
  };

  const getContentType = () => {
    if (isImage) return amplitudeContentTypes.aiImage;
    if (isPodcast) return amplitudeContentTypes.aiAudio;
    return amplitudeContentTypes.aiDocument;
  };

  const getArtifactPath = (basePath: string) => {
    if (isImage) return `${basePath}?imageId=${id}`;
    if (isPodcast) return basePath;
    return `${basePath}?edit=true&id=${id}`;
  };

  const navigateToArtifact = () => {
    if (!conversationId) return;

    services.track.ampliTrackAIAssistantPastChatOpenedEvent({
      conversationId,
      pageSource: aiAssistantPageSource.myContent,
      contentType: getContentType(),
    });

    if (isPodcast) preparePodcastNavigation(isGeneratingPodcast, id);
    router.push(getArtifactPath(`/${routes.aiTools.root}/${conversationId}`));
  };

  const handleClick = createNonPropagatingEventHandler(navigateToArtifact);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick(event);
    }
  };

  const renderContent = () => {
    if (isGeneratingPodcast) {
      return (
        <Box
          data-testid='artifact-card-loading-content'
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <CircularProgressFixed size={32} />
        </Box>
      );
    }
    if (isImage) {
      return presignedUrl
        ? <ArtifactCardImageContent presignedUrl={presignedUrl} title={title} />
        : null;
    }
    return <ArtifactCardContent content={getTextContent()} />;
  };

  return (
    <Card
      data-testid='artifact-card'
      role='button'
      tabIndex={0} // it should be focusable in sequential keyboard navigation
      aria-label={`artifact-card-${title}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        height: customSizes.artifactCard.cardHeight,
        overflow: 'hidden',
        border: `1px solid ${palette.elevation.outlined}`,
        borderRadius: borderRadius.md,
        boxShadow: boxShadows.elevation1,
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: boxShadows.elevation6,
        },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ArtifactCardHeader title={title} createdAt={headerCreatedAt} type={type} />
      {renderContent()}
    </Card>
  );
};

export default ArtifactCard;
